import { ChatMessages } from "@storytell/cp-client";
import {
  type StreamingBlock,
  type StreamingChatId,
  type StreamingOperation,
  type StreamingOperationId,
  determinePreferredRenderer,
} from "@storytell/streaming";
import { BlockRenderer as BlockRendererEnum } from "@storytell/streaming";
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { BlockRenderer } from "./BlockRenderer";
import { OperationHeader, ThinkingRenderer, TurnCompleteRenderer } from "./r";
import { ToolMatchesSection } from "./r/internal/ToolMatchesHighlight";

const shouldAutoCollapse = (operation: StreamingOperation | undefined) => {
  if (!operation || !operation.isComplete) {
    return false;
  }
  if (
    operation.activity === "planning_v1" ||
    operation.activity === "evaluator_v1" ||
    operation.activity === "user_kb_search_v1" ||
    operation.activity === "web_search_v1" ||
    operation.activity === "web_search"
  ) {
    return true;
  }
  // Don't auto-collapse thinking operations - let ThinkingRenderer handle it
  return false;
};

export const OperationRenderer = (props: {
  operationId: StreamingOperationId;
  index: number;
  chatId: StreamingChatId;
}) => {
  const wire = useWire();
  const stream = wire.stream;

  const operation = () => stream.operation.get(props.operationId);
  const hasNextOperation = () => {
    const op = operation();
    if (!op) return false;
    const operations = stream.message.get(op.messageId)?.operations;
    if (!operations) return false;
    if (props.index < operations.length - 1) return true;
    return false;
  };

  let manuallyExpanded = false;
  const [isExpanded, setIsExpanded] = createSignal(!shouldAutoCollapse(operation()));

  const shouldCollapse = createMemo(() => shouldAutoCollapse(operation()));
  createEffect(() => {
    if (manuallyExpanded) return;
    if (shouldCollapse()) {
      setIsExpanded(false);
    }
  });

  const isProcessing = () => operation()?.status === ChatMessages.OperationStatus.RUNNING;

  const shouldShowOperation = () => {
    // Filter out heartbeat operations
    const op = operation();
    if (op?.activity === "system_heartbeat") {
      return false;
    }

    if (!op?.isComplete) return true;
    // Hide evaluator_v1 operations after they're complete
    if (op?.activity === "evaluator_v1") return false;
    return true;
  };

  const shouldShowHeader = () => {
    if (operation()?.activity === "conversation_complete") return false;
    // Show header for all operations including thinking
    return true;
  };
  // const shouldShowBlocks = () => {
  //   if (!operation()?.isComplete) return true;
  //   // Hide blocks for planning_v1 operations after they're complete
  //   if (operation()?.activity === "planning_v1") return false;
  //   return true;
  // };

  const title = () => {
    const activity = operation()?.activity;
    const isComplete = operation()?.isComplete;
    if (activity === "planning_v1") return "Thinking";
    if (activity === "user_kb_search_v1")
      return !isComplete ? "Searching through your Knowledge Base" : "Searched your Knowledge";
    if (activity === "web_search_v1") return !isComplete ? "Searching the Web" : "Searched the Web";
    if (activity === "web_search") return !isComplete ? "Searching the Web" : "Searched the Web";
    if (activity === "artifact_v1") return !isComplete ? "Generating Artifact" : "Created Artifact";
    if (activity === "artifact_generation") return !isComplete ? "Generating Artifact" : "Created Artifact";
    if (activity === "read_concepts") return !isComplete ? "Reading Concepts" : "Read Concepts";
    if (activity === "stream_response_v1") return "Answer";
    if (activity === "direct_response_v1") return "Answer";
    if (activity === "answer") return "Answer";
    return operation()?.name || "";
  };

  const blocks = () => {
    const allBlocks: StreamingBlock[] = [];
    for (const blockId of operation()?.blocks ?? []) {
      const block = stream.block.get(blockId);
      if (block) {
        // Hide any non-artifact blocks from the artifact operation to clean up the messy UI
        if (
          operation()?.activity === "artifact_generation" &&
          block.isComplete &&
          determinePreferredRenderer(block) !== BlockRendererEnum.ARTIFACT_VIEWER
        ) {
          continue;
        }
        allBlocks.push(block);
      }
    }
    return allBlocks;
    // operation()
    //   ?.blocks.map((b) => stream.block.get(b))
    //   .filter((b): b is StreamingBlock => b !== undefined);
  };

  return (
    <Show when={shouldShowOperation() && operation()}>
      {(op) => (
        <>
          {/* Use ThinkingRenderer for thinking operations */}
          <Show when={op().activity === "thinking"}>
            <ThinkingRenderer operation={op()} chatId={props.chatId} />
          </Show>

          {/* Regular operation rendering for non-thinking operations */}
          <Show when={op().activity !== "thinking"}>
            <div data-operation-id={op().operationId} data-renderer="stream-renderer-operation-block" class="relative">
              {/* Timeline connector - extends through entire operation height */}
              <Show when={hasNextOperation()}>
                <div
                  class="absolute bottom-0 w-0.5 min-h-2 -ml-2 border-l-2 border-dotted border-gray-300 dark:border-slate-600"
                  style={{
                    left: "24px",
                    top: "32px",
                  }}
                />
              </Show>

              <Show when={shouldShowHeader()}>
                <OperationHeader
                  operation={op()}
                  title={title()}
                  isExpanded={isExpanded()}
                  isProcessing={isProcessing()}
                  hasBlocks={op()?.blocks.length > 0}
                  onToggle={() => {
                    setIsExpanded((prev) => !prev);
                    manuallyExpanded = true;
                  }}
                  chatId={props.chatId}
                />
              </Show>

              {/* Blocks - each block renders independently */}
              <Show when={isExpanded()}>
                <div class="flex flex-col space-y-3 pb-3 min-w-0 pl-10">
                  <For each={blocks()}>{(block) => <BlockRenderer block={block} chatId={props.chatId} />}</For>

                  <Show when={blocks()?.length === 0 && isProcessing()}>
                    <div class="text-sm text-gray-500 dark:text-gray-400 italic ml-12">Planning and preparing...</div>
                  </Show>

                  {/* TODO: @andi streaming */}
                  <Show when={op().activity === "planning_v1"}>
                    <ToolMatchesSection chatId={props.chatId} />
                  </Show>
                </div>

                <Show when={op()?.activity === "conversation_complete"}>
                  <TurnCompleteRenderer block={{ operationId: op().operationId }} chatId={props.chatId} />
                </Show>
              </Show>
            </div>
          </Show>
        </>
      )}
    </Show>
  );
};
